// libraries.
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import styled from 'styled-components';
import { electricPurple600, robotoRegular, scarpaGray400 } from '@habitech/shared';

// components.
import { SimpleInputField } from '@shared/SimpleInputField';

const InputWrapper = styled.div`
  input::placeholder {
    color: #aaa;
    opacity: 1;
  }
`;

const SavedCoordsBox = styled.div`
  margin-top: 12px;
  padding: 8px 10px;
  border-radius: 6px;
  background-color: #f5f5f5;
  border-left: 3px solid ${electricPurple600};
`;

const SavedCoordsLabel = styled.p`
  font-family: ${robotoRegular};
  font-size: 14px;
  color: ${scarpaGray400};
  margin: 0 0 4px 0;
  letter-spacing: 0.4px;
`;

const SavedCoordsValue = styled.p`
  font-family: ${robotoRegular};
  font-size: 14px;
  color: #333;
  margin: 0;
  font-variant-numeric: tabular-nums;
`;

// hooks.
import { useGetLeadDetailInfoQuery } from '@domains/lead-detail';

// selectors.
import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';
import {
  getSelectedCoordinates,
  getSelectedNid,
  updateCoordinates,
} from '../store/slices/georeferencing-slice';

interface CoordinateInputsProps {
  onInputChange?: (hasValue: boolean) => void;
}

export const CoordinateInputs = ({ onInputChange }: CoordinateInputsProps) => {
  const dispatch = useDispatch();
  const selectedCoordinates = useSelector(getSelectedCoordinates);
  const nid = useSelector(getSelectedNid);
  const country = useSelector(getCountry);

  const { data: leadDetail } = useGetLeadDetailInfoQuery(
    { country, nid: nid || '' },
    { skip: !nid }
  );

  const [latitude, setLatitude] = useState<string>('');
  const [longitude, setLongitude] = useState<string>('');
  const [errors, setErrors] = useState<{ lat?: string; lng?: string }>({});

  // Solo limpia los inputs cuando se resetea tras un guardado exitoso
  useEffect(() => {
    if (!selectedCoordinates) {
      setLatitude('');
      setLongitude('');
      setErrors({});
      onInputChange?.(false);
    }
  }, [selectedCoordinates]);

  const validateCoordinate = (value: string, type: 'lat' | 'lng'): boolean => {
    // If empty, clear errors for this field
    if (!value || value === '') {
      setErrors(prev => ({ ...prev, [type]: undefined }));
      return false;
    }

    const num = parseFloat(value);

    // parseFloat can parse partial numbers, so we need to check if it's a complete valid number
    if (isNaN(num) || value.trim() === '') {
      setErrors(prev => ({ ...prev, [type]: undefined }));
      return false;
    }

    if (type === 'lat' && (num < -90 || num > 90)) {
      setErrors(prev => ({
        ...prev,
        lat: 'La latitud debe estar entre -90 y 90',
      }));
      return false;
    }

    if (type === 'lng' && (num < -180 || num > 180)) {
      setErrors(prev => ({
        ...prev,
        lng: 'La longitud debe estar entre -180 y 180',
      }));
      return false;
    }

    setErrors(prev => ({ ...prev, [type]: undefined }));
    return true;
  };

  const handleLatitudeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLatitude(value);

    if (value && validateCoordinate(value, 'lat')) {
      const lat = parseFloat(value);
      const lng = longitude ? parseFloat(longitude) : 0;
      dispatch(updateCoordinates({ lat, lng }));
      onInputChange?.(true);
    } else if (!value) {
      onInputChange?.(!longitude);
    }
  };

  const handleLongitudeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLongitude(value);

    if (value && validateCoordinate(value, 'lng')) {
      const lat = latitude ? parseFloat(latitude) : 0;
      const lng = parseFloat(value);
      dispatch(updateCoordinates({ lat, lng }));
      onInputChange?.(true);
    } else if (!value) {
      onInputChange?.(!latitude);
    }
  };

  const savedCoords = leadDetail?.location?.coordinates;
  const hasSavedCoords = savedCoords?.length === 2;

  return (
    <div>
      <InputWrapper>
        <Row>
          <Col xs={12} className="mb-2">
            <SimpleInputField
              label="Latitud"
              name="latitude"
              type="number"
              step="any"
              value={latitude}
              onChange={handleLatitudeChange}
              placeholder="Ingresa latitud"
              error={errors.lat}
            />
          </Col>
          <Col xs={12}>
            <SimpleInputField
              label="Longitud"
              name="longitude"
              type="number"
              step="any"
              value={longitude}
              onChange={handleLongitudeChange}
              placeholder="Ingresa longitud"
              error={errors.lng}
            />
          </Col>
        </Row>
      </InputWrapper>

      {hasSavedCoords && (
        <SavedCoordsBox>
          <SavedCoordsLabel>Coordenadas default</SavedCoordsLabel>
          <SavedCoordsValue>Lat: {savedCoords![0]}</SavedCoordsValue>
          <SavedCoordsValue>Lng: {savedCoords![1]}</SavedCoordsValue>
        </SavedCoordsBox>
      )}
    </div>
  );

};
